/**
 * Network primitives for the offline-first boot path.
 *
 * Per shared-docs/offline-first-architecture.md §"Step 1": every auth and
 * network call on app start must run against a timeout so the loading
 * spinner can't hang forever when there's no connection. Wealth Tracker
 * Build 18 was the canonical example — `fetchAuthSession()` and
 * `fetchUserAttributes()` against Cognito never resolved offline.
 */

export class NetworkError extends Error {
  constructor(message = 'Network error') {
    super(message);
    this.name = 'NetworkError';
    this.isNetworkError = true;
  }
}

export class TimeoutError extends Error {
  constructor(message = 'Timed out') {
    super(message);
    this.name = 'TimeoutError';
    this.isTimeoutError = true;
  }
}

export function withTimeout(promise, ms, message) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(
      () => reject(new TimeoutError(message || `Timed out after ${ms}ms`)),
      ms
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export function isOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function readCachedTokens() {
  let lastAuthUser = null;
  let clientId = null;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    const m = key.match(/^CognitoIdentityServiceProvider\.([^.]+)\.LastAuthUser$/);
    if (m) {
      clientId = m[1];
      lastAuthUser = localStorage.getItem(key);
      break;
    }
  }
  if (!lastAuthUser || !clientId) return null;

  const idToken = localStorage.getItem(
    `CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.idToken`
  );
  if (!idToken) return null;
  return { idToken, lastAuthUser, clientId };
}

/**
 * Reads Cognito tokens straight out of localStorage. Lets the app keep a
 * signed-in user logged in even when `Auth.currentAuthenticatedUser()` times
 * out (offline cold-launch, captive portal, flaky 5G). The token may be
 * expired — that's the sync engine's problem to refresh on next online call.
 */
export function getCachedAuthUser() {
  try {
    const tokens = readCachedTokens();
    if (!tokens) return null;

    const parts = tokens.idToken.split('.');
    if (parts.length !== 3) return null;
    const payloadJson = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(payloadJson);

    return {
      sub: payload.sub,
      username: tokens.lastAuthUser,
      email: payload.email,
      exp: payload.exp,
    };
  } catch {
    return null;
  }
}

/**
 * Returns the raw idToken JWT from localStorage, or null if none cached.
 * Used as a fallback when `Auth.currentSession()` times out on Capacitor —
 * without this fallback the request would go out with no Authorization
 * header, hit a 401 from the Cognito authorizer (no CORS headers on 401),
 * and surface in the WKWebView as a generic "Network Error".
 */
export function getCachedIdToken() {
  try {
    return readCachedTokens()?.idToken || null;
  } catch {
    return null;
  }
}
